import ui from './ui.module.css';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAccountKey } from '../lib/account';
import { AppearanceControl } from '../lib/Appearance';
import type { AvatarKind } from '@circles/convex';
import { AvatarControls } from '../lib/AvatarControls';
import { avatarTint } from '../lib/avatarTint';
import { presetOf } from '../lib/AvatarPicker';
import { api, useMutation } from '../lib/convex';
import { MemberAvatar } from '../lib/MemberAvatar';
import { useOwnerJoinOffer } from '../lib/ownerJoin';
import { useModalDialog } from '../lib/useModalDialog';
import { ChevronRightIcon } from './icons';
import styles from './SettingsSheet.module.css';

export type DeviceActions = {
  circleName: string;
  memberName: string;
  /** The member's own id and picture, for the picture control (§14.79). */
  memberId: string;
  avatarUrl: string;
  avatarKind: AvatarKind;
  avatarHue: number;
  /** Authorises the member's own avatar mutations (never set for previews: `device` is null there). */
  deviceToken: string;
  onSwitch: () => void;
  onLeave: () => void;
};

/**
 * Bottom sheet from the home top bar: appearance, who this device is in the current circle, joining
 * another circle (and, with a builder key, the owner's own circles), and a quiet link to the builder
 * at the bottom (kids see this sheet too).
 */
export function SettingsSheet({
  open,
  onClose,
  onJoined,
  device,
}: {
  open: boolean;
  onClose: () => void;
  /** After the owner's own circles joined from here (the launcher shows the first one). */
  onJoined: () => void;
  device: DeviceActions | null;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const accountKey = useAccountKey();
  const hasBuilderKey = accountKey !== null;
  const offer = useOwnerJoinOffer(accountKey);
  const uploadUrl = useMutation(api.members.myAvatarUploadUrl);
  const setAvatar = useMutation(api.members.setMyAvatar);
  const setAvatarPreset = useMutation(api.members.setMyAvatarPreset);
  const setAvatarHue = useMutation(api.members.setMyAvatarHue);
  const removeAvatar = useMutation(api.members.removeMyAvatar);

  const shown = useModalDialog(ref, open, onClose);
  useEffect(() => {
    if (!shown) setConfirmLeave(false);
  }, [shown]);

  return (
    <dialog
      ref={ref}
      className={styles.sheet}
      aria-labelledby="settings-title"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {shown && (
        <div className={styles.inner}>
          <div className={styles.grabber} aria-hidden />
          <header className={styles.header}>
            <h2 id="settings-title" className={styles.title}>
              Settings
            </h2>
            <button className={styles.close} onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>

          <AppearanceControl />

          {device && (
            <section className={styles.group} aria-labelledby="settings-device">
              <h3 id="settings-device" className={styles.groupTitle}>
                This device
              </h3>
              <p className={styles.groupLead}>
                Set up as <strong>{device.memberName}</strong> in {device.circleName}.
              </p>
              <div className={styles.photo}>
                <AvatarControls
                  name={device.memberName}
                  avatarKind={device.avatarKind}
                  preset={presetOf(device.avatarUrl, device.avatarKind)}
                  hue={device.avatarHue}
                  uploadLabel="Use a photo"
                  preview={
                    <MemberAvatar
                      name={device.memberName}
                      avatarUrl={device.avatarUrl}
                      className={styles.photoAvatar}
                      style={{ background: avatarTint(device.avatarHue) }}
                    />
                  }
                  uploader={{
                    getUploadUrl: () => uploadUrl({ deviceToken: device.deviceToken }),
                    setAvatar: (storageId) => setAvatar({ deviceToken: device.deviceToken, storageId }),
                  }}
                  setPreset={(preset) => setAvatarPreset({ deviceToken: device.deviceToken, preset })}
                  setHue={(hue) => setAvatarHue({ deviceToken: device.deviceToken, hue })}
                  remove={() => removeAvatar({ deviceToken: device.deviceToken })}
                  buttonClassName={styles.photoButton}
                  dangerClassName={`${styles.photoButton} ${styles.photoRemove}`}
                />
              </div>
              {confirmLeave ? (
                <div className={styles.confirm}>
                  <p>Remove {device.circleName} from this device? You'll need the invite link to come back.</p>
                  <div className={styles.confirmActions}>
                    <button className={ui.secondary} onClick={() => setConfirmLeave(false)}>
                      Cancel
                    </button>
                    <button className={`${ui.button} ${styles.dangerButton}`} onClick={device.onLeave}>
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.rows}>
                  <button className={styles.row} onClick={device.onSwitch}>
                    <span>Not {device.memberName}? Switch</span>
                    <ChevronRightIcon className={styles.rowChevron} />
                  </button>
                  <button className={`${styles.row} ${styles.rowDanger}`} onClick={() => setConfirmLeave(true)}>
                    <span>Leave on this device</span>
                  </button>
                </div>
              )}
            </section>
          )}

          <section className={styles.group} aria-labelledby="settings-circles">
            <h3 id="settings-circles" className={styles.groupTitle}>
              Circles
            </h3>
            <div className={styles.rows}>
              {offer.circles.length > 0 && (
                <button
                  className={`${styles.row} ${styles.rowAccent}`}
                  disabled={offer.busy}
                  onClick={() => void offer.join().then((ok) => ok && onJoined())}
                >
                  <span>{offer.busy ? 'Joining…' : offer.label}</span>
                </button>
              )}
              <Link to="/join" className={styles.row}>
                <span>Join another circle</span>
                <ChevronRightIcon className={styles.rowChevron} />
              </Link>
            </div>
            {offer.error && <p className={styles.error}>{offer.error}</p>}
          </section>

          <Link to="/builder" className={styles.builderLink} data-testid="settings-builder-link">
            {hasBuilderKey ? 'Your builder' : 'Run your own circle'}
            <ChevronRightIcon className={styles.rowChevron} />
          </Link>
        </div>
      )}
    </dialog>
  );
}
